import { useEffect, useState } from 'react';

import { Edit, Check } from '@mui/icons-material';
import { Stack, Typography, TextField, IconButton, Box } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { v4 as uuidv4 } from 'uuid';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { setSendName, setSmsSenderProfileId } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { channelIcons } from 'domains/campaigns/pages/CurationsPage/utils';
import { SmsSendInputState, SplitTypes } from 'domains/campaigns/types';
import { SMSSenderProfile } from 'domains/content/types';
import FormLayout from 'domains/core/components/FormLayout';
import InputLabel from 'domains/core/components/InputLabel';
import Select from 'domains/core/components/Select';
import useSMSSenderProfiles from 'hooks/queries/useSMSSenderProfiles';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';
import { formatPhoneNumber } from 'utils';

import ContentWrapper from './ContentWrapper';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    dispatchCampaignAction: React.Dispatch<any>;
    setContentPreviewId: (id: number) => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const SmsContentStep = ({
    activeSendIndex,
    campaignState,
    contentPreviewId,
    dispatchCampaignAction,
    setContentPreviewId,
    setIsContinueEnabled,
    setNextStep,
    onContentUnavailableChange,
}: Props) => {
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const {
        smsSender: hasSmsSenderFlag,
        multipleSendsConditionalSplit: hasMultipleSendsConditionalSplitFlag,
    } = useFlags();

    // Skip send name updates when editing existing draft/paused curations
    const isEditingExistingDraftOrPaused = status === CampaignStatus.DRAFT || status === CampaignStatus.PAUSED;

    // Edit sendname feature is disabled for non-A/B testing flows
    const editSendnameEnabled = false;
    const currentSend = campaignState.sends[activeSendIndex] as SmsSendInputState;
    const { content, name: sendName, smsSenderProfileId } = currentSend;

    // Only fetch sender profiles when feature flag is enabled
    const { isInitialLoading: isSenderProfilesLoading, data: senderProfiles } = useSMSSenderProfiles({
        type: 'sms',
        reactQueryOptions: { enabled: !!hasSmsSenderFlag },
    });

    // Auto-select sender profile if there's only one option
    useEffect(() => {
        if (hasSmsSenderFlag && !isSenderProfilesLoading && senderProfiles?.length === 1 && !smsSenderProfileId) {
            dispatchCampaignAction(setSmsSenderProfileId(senderProfiles[0].id, activeSendIndex));
        }
    }, [
        hasSmsSenderFlag,
        isSenderProfilesLoading,
        senderProfiles,
        smsSenderProfileId,
        activeSendIndex,
        dispatchCampaignAction,
    ]);

    // State for editable send name
    const [isEditingName, setIsEditingName] = useState(false);
    const curationName = campaignState.name || '';
    const contentName = content?.name || '';

    // Generate the default name with format: {curationName} - {contentName}.{uuid}
    const generateDefaultName = (cntName?: string) => {
        const effectiveContentName = cntName ?? contentName;
        const uuid = uuidv4();
        const curationPart = curationName || '';
        const contentPart = effectiveContentName || '';

        return `${curationPart} - ${contentPart}.${uuid}`;
    };

    // Get the display name: use sendName from campaignState if available, otherwise use default
    const getDisplayName = () => {
        if (sendName && sendName.trim() !== '') {
            return sendName;
        }
        return generateDefaultName();
    };
    const [customName, setCustomName] = useState<string>(getDisplayName());

    // Validation: Check if name is duplicate
    const isDuplicateName = (() => {
        if (customName.trim() === '') return false;
        // Check if any other send has the same name
        return campaignState.sends.some((send, index) => index !== activeSendIndex && send.name === customName);
    })();
    const hasNameError = isDuplicateName;
    const nameErrorMessage = isDuplicateName ? 'All send names must be different' : '';

    // Sync local state with campaignState when sendName changes
    useEffect(() => {
        setCustomName(getDisplayName());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendName, activeSendIndex]);

    // Initialize send name in campaignState if it's empty or update when content changes
    // Skip for existing draft/paused curations to preserve existing names
    useEffect(() => {
        if (isEditingExistingDraftOrPaused) return;

        if (!sendName || sendName.trim() === '') {
            const defaultName = generateDefaultName();
            dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex, curationName, contentName, isEditingExistingDraftOrPaused]);

    // Reset send name when content changes
    // Skip for existing draft/paused curations to preserve existing names
    useEffect(() => {
        if (isEditingExistingDraftOrPaused) return;

        if (contentName) {
            const newDefaultName = generateDefaultName(contentName);
            dispatchCampaignAction(setSendName(newDefaultName, activeSendIndex));
            setCustomName(newDefaultName);
        } else if (!content?.id) {
            // Content was cleared - format: {curationName} - .{uuid}
            const defaultName = `${curationName} - .${uuidv4()}`;
            dispatchCampaignAction(setSendName(defaultName, activeSendIndex));
            setCustomName(defaultName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content?.id, isEditingExistingDraftOrPaused]);

    useEffect(() => {
        // A plain (non-mixed) SMS curation is always a single send, so its root (index 0) goes
        // straight to scheduling. In a mixed-channel curation, though, SMS can be the root or any
        // mid-tree send just like Email — it must reach CHOOSE_NEXT_STEP so time delay/conditional
        // split are offered, exactly as Email's own EmailContentStep does unconditionally.
        // For a MEETS branch root specifically, route to CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT so the
        // user can "proceed to does not meet criteria" without losing their content — the generic
        // CHOOSE_NEXT_STEP has no such route for non-email sends.
        const currentSend = campaignState.sends[activeSendIndex] as any;
        if (currentSend?.splitType === SplitTypes.MEETS) {
            setNextStep(
                hasMultipleSendsConditionalSplitFlag
                    ? CustomCampaignSteps.CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT
                    : CustomCampaignSteps.SELECT_PATH_ACTION
            );
        } else if (activeSendIndex > 0 || campaignState.isMixedChannel) {
            setNextStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
        } else {
            setNextStep(CustomCampaignSteps.SCHEDULE_CURATION);
        }
        // The dependency array is empty because this useEffect should run exactly once after the initial rendering of the component.
        // The hook is not dependent on any props or state variables to re-run. This is intended to perform the initial setup based on
        // the `activeSendIndex` value when the component mounts without reacting to any further updates or re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Disable continue if there's a name error
        if (hasNameError) {
            setIsContinueEnabled(false);
            return;
        }
        // Disable continue if sender profile is required but not selected
        const isSenderProfileValid = !hasSmsSenderFlag || !!smsSenderProfileId;
        setIsContinueEnabled(!!content.id && isSenderProfileValid);
    }, [setIsContinueEnabled, content.id, hasNameError, hasSmsSenderFlag, smsSenderProfileId]);

    // Handlers for editing send name
    const handleEditClick = () => {
        setIsEditingName(true);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomName(event.target.value);
    };

    const handleNameSave = () => {
        // Don't save if name is duplicate
        if (hasNameError) {
            return;
        }

        setIsEditingName(false);
        // Dispatch action to save customName to campaignState
        dispatchCampaignAction(setSendName(customName, activeSendIndex));
    };

    const handleNameKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleNameSave();
        }
    };

    const StepIcon = channelIcons.SMS;

    return (
        <FormLayout
            header="select content"
            icon={<StepIcon />}
            guidingText="Focus on the goal of the customer interaction and choose content from the dropdown that supports your objective."
            stepName="text message"
        >
            <Stack rowGap={4}>
                {/* Editable send name section - only shown when feature flag is enabled */}
                {editSendnameEnabled && (
                    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {isEditingName ? (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <TextField
                                            value={customName}
                                            onChange={handleNameChange}
                                            onKeyPress={handleNameKeyPress}
                                            onBlur={handleNameSave}
                                            autoFocus
                                            multiline
                                            variant="standard"
                                            error={hasNameError}
                                            helperText={nameErrorMessage}
                                            sx={{
                                                width: '100%',
                                                '& .MuiInputBase-input': {
                                                    fontSize: '1.25rem',
                                                    fontWeight: 'bold',
                                                    wordBreak: 'break-word',
                                                },
                                            }}
                                        />
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={handleNameSave}
                                        disabled={hasNameError}
                                        sx={{ padding: 0.5, mt: 0.5, flexShrink: 0 }}
                                    >
                                        <Check fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {customName}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={handleEditClick}
                                            sx={{ padding: 0.5, flexShrink: 0 }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    {hasNameError && (
                                        <Typography variant="caption" color="error" fontSize={12}>
                                            {nameErrorMessage}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
                <ContentWrapper
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    contentPreviewId={contentPreviewId}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setContentPreviewId={setContentPreviewId}
                    onContentUnavailableChange={onContentUnavailableChange}
                />
                {hasSmsSenderFlag && (
                    <Select
                        data-testid="sms-sender-profile-select"
                        fullWidth
                        label={
                            <Box
                                component="span"
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    overflow: 'visible',
                                    pointerEvents: 'auto',
                                    display: 'inline-block',
                                }}
                            >
                                <InputLabel label="Sender" labelIconTooltipText="Who is sending Text Message?" />
                            </Box>
                        }
                        menuItemStyles={{ textTransform: 'none' }}
                        onChange={(e: any) =>
                            dispatchCampaignAction(setSmsSenderProfileId(e.target.value, activeSendIndex))
                        }
                        required
                        value={smsSenderProfileId ?? ''}
                        values={
                            !isSenderProfilesLoading
                                ? senderProfiles?.map((senderProfile: SMSSenderProfile) => ({
                                      value: senderProfile.id,
                                      label:
                                          `${senderProfile.name} <${senderProfile.countryCode} ` +
                                          formatPhoneNumber(senderProfile.number) +
                                          `>`,
                                  }))
                                : []
                        }
                    />
                )}
            </Stack>
        </FormLayout>
    );
};

export default SmsContentStep;
